import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as userService from '../../services/user';
import * as addressService from '../../services/address';

// Async thunks
export const fetchUsers = createAsyncThunk(
  'user/fetchUsers',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await userService.getUsers(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUser = createAsyncThunk(
  'user/fetchUser',
  async (sid, { rejectWithValue }) => {
    try {
      const response = await userService.getUser(sid);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createUser = createAsyncThunk(
  'user/createUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await userService.createUser(userData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateUser = createAsyncThunk(
  'user/updateUser',
  async ({ sid, userData }, { rejectWithValue }) => {
    try {
      const response = await userService.updateUser(sid, userData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const promoteUser = createAsyncThunk(
  'user/promoteUser',
  async ({ sid, userData }, { rejectWithValue }) => {
    try {
      return await userService.promoteUser(sid, userData);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const demoteUser = createAsyncThunk(
  'user/demoteUser',
  async (sid, { rejectWithValue }) => {
    try {
      return await userService.demoteUser(sid);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteUser = createAsyncThunk(
  'user/deleteUser',
  async (id, { rejectWithValue }) => {
    try {
      await userService.deleteUser(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUserStats = createAsyncThunk(
  'user/fetchUserStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await userService.getUserStats();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Address thunks
export const fetchAddresses = createAsyncThunk(
  'user/fetchAddresses',
  async (_, { rejectWithValue }) => {
    try {
      const response = await addressService.getAddresses();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createAddress = createAsyncThunk(
  'user/createAddress',
  async (addressData, { rejectWithValue }) => {
    try {
      const response = await addressService.createAddress(addressData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateAddress = createAsyncThunk(
  'user/updateAddress',
  async ({ id, addressData }, { rejectWithValue }) => {
    try {
      const response = await addressService.updateAddress(id, addressData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  users: [],
  currentUser: null,
  stats: null,
  loading: false,
  error: null,
  totalUsers: 0,
  addresses: [],
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentUser: (state) => {
      state.currentUser = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        // Handle paginated response properly
        if (action.payload && action.payload.results) {
          state.users = action.payload.results;
          state.totalUsers = action.payload.total || 0;
        } else {
          state.users = action.payload || [];
          state.totalUsers = 0;
        }
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch single user
      .addCase(fetchUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload;
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create user
      .addCase(createUser.fulfilled, (state, action) => {
        state.users.push(action.payload);
      })
      // Update user
      .addCase(updateUser.fulfilled, (state, action) => {
        const index = state.users.findIndex(u => u.sid === action.payload.sid);
        if (index !== -1) {
          state.users[index] = action.payload;
        } else {
          // If user not found in current list, add it
          state.users.push(action.payload);
        }
        if (state.currentUser?.sid === action.payload.sid) {
          state.currentUser = action.payload;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(promoteUser.fulfilled, (state, action) => {
        const index = state.users.findIndex(u => u.sid === action.payload.sid);
        if (index !== -1) state.users[index] = action.payload;
      })
      .addCase(promoteUser.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(demoteUser.fulfilled, (state, action) => {
        const index = state.users.findIndex(u => u.sid === action.payload.sid);
        if (index !== -1) state.users[index] = action.payload;
      })
      .addCase(demoteUser.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Delete user
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users = state.users.filter(u => u.sid !== action.payload);
        if (state.currentUser?.sid === action.payload) {
          state.currentUser = null;
        }
      })
      // Fetch user stats
      .addCase(fetchUserStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      // Address cases
      .addCase(fetchAddresses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAddresses.fulfilled, (state, action) => {
        state.loading = false;
        // Support both paginated and plain array responses
        if (Array.isArray(action.payload)) {
          state.addresses = action.payload;
        } else if (action.payload && Array.isArray(action.payload.results)) {
          state.addresses = action.payload.results;
        } else {
          state.addresses = [];
        }
      })
      .addCase(fetchAddresses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createAddress.fulfilled, (state, action) => {
        state.addresses.push(action.payload);
      })
      .addCase(updateAddress.fulfilled, (state, action) => {
        const idx = state.addresses.findIndex(a => a.id === action.payload.id);
        if (idx !== -1) {
          state.addresses[idx] = action.payload;
        }
      });
  },
});

export const { clearError, clearCurrentUser } = userSlice.actions;
export default userSlice.reducer;
